import {gsap} from "gsap";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {BrickSnake2000} from "../../BrickSnake2000";
import {BrickSnake2000WildSymbol} from "../../reels/symbols/WildSymbol";
import {BrickSnake2000Symbol, SymbolArgs} from "../../reels/symbols/BrickSnake2000Symbol";
import {FreespinsMultiplierSymbol} from "../../reels/symbols/FreespinsMultiplierSymbol";
import {MoveSnakes} from "../maingame/MoveSnakes";
import {CollectorSymbol} from "../../reels/symbols/CollectorSymbol";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {MaxWinSymbol} from "../../reels/symbols/MaxWinSymbol";
import {BrickSnake2000GameAssets} from "../../BrickSnake2000GameAssets";
import {MultipleSymbolsThing} from "../../reels/BrickSnake2000FreespinsReelView";

export class FreespinSnakes implements SimplePresentation {
    private snakeSoundIndex:number;

    name:string = "FreespinSnakes";

    constructor() {
        this.snakeSoundIndex = 0;
    }

    shouldRun():boolean {
        return BrickSnake2000.model.gameData.snakes.length > 0;
    }

    // methods were invented for a reason, use them :skull:
    start(controller:SimplePresentationRunnerController):void {
        const tl = gsap.timeline({
            onComplete: () => {
                controller.setSkipCallback();
                controller.finish()
            }
        });

        const snakeData = BrickSnake2000.model.getFreespinsSnakeData();
        const snakePaths = snakeData.snakes;
        const totalLength = snakeData.totalLength;
        const snakeIDs = snakeData.index;
        const alreadyEatenSymbols = new Set<any>();
        const snakeVisitedPositions:boolean[][] = [[],[],[],[],[]];
        for (let i = 0; i < 5; i++)
            for (let j = 0; j < 5; j++)
                snakeVisitedPositions[i][j] = false;


        for (let posIndex = 1; posIndex < totalLength; posIndex++) {
            const stepTl = gsap.timeline();
            for (let snakeIndex = 0; snakeIndex < snakePaths.length; snakeIndex++) {
                const currentSnake = snakePaths[snakeIndex];
                if (currentSnake[posIndex] == undefined || currentSnake[posIndex - 1] == undefined)
                    continue;

                const startIndex = Math.min(...Object.keys(currentSnake).map((key) => parseInt(key)));
                const pos = currentSnake[posIndex];
                const prevPos = currentSnake[posIndex - 1];
                const reelView = BrickSnake2000.freespinsReelView.getReelModel(pos.reelID, pos.reelPosition);
                const reelViewPrev = BrickSnake2000.freespinsReelView.getReelModel(prevPos.reelID, prevPos.reelPosition);
                const oldSymbol = reelView.reelModel.getVisibleSymbolAt(0);
                const oldSymbolName = oldSymbol.symbolName;
                const symbolArgs:SymbolArgs = {
                    symbolName: "W",
                    isSpinning: false,
                    isStopping: false,
                    reelsView: reelView,
                    reelPosition: pos.reelPosition,
                    reelID: pos.reelID,
                    symbolSize: 1,
                };

                const isPrevTurned = (posIndex - startIndex) > 1 && ((currentSnake[posIndex - 2].reelPosition != currentSnake[posIndex].reelPosition) &&
                                                                     (currentSnake[posIndex - 2].reelID != currentSnake[posIndex].reelID));
                const prevSymbolNewPart = (posIndex - startIndex) == 1
                                          ? "_Tail"
                                          : isPrevTurned ? "_Knee" : "_Body";

                const prevRotation = isPrevTurned
                                     ? MoveSnakes.getTurnedRotation(currentSnake[posIndex - 2], prevPos, pos)
                                     : MoveSnakes.getRotation(prevPos, pos);
                const rotation = MoveSnakes.getRotation(prevPos, pos);
                const cameFromDirection = MoveSnakes.getPreviousDirection(prevPos, pos);
                const snakeID = snakeIDs[snakeIndex];
                const snakeSkin = snakeID == 0
                                 ? "_A"
                                 : snakeID == 1
                                 ? "_B"
                                 : "_C";

                const brokeWall = MoveSnakes.brokeWall(prevPos, pos);
                const wasFinalStep = currentSnake[posIndex + 1] == undefined && !BrickSnake2000.model.data.brokeTheBank;
                const snakeHasNotBeenHere = !snakeVisitedPositions[pos.reelID][pos.reelPosition];

                const cameFromEgg = currentSnake[posIndex - 2] == undefined && posIndex > 1;
                const canBeEaten = !((oldSymbolName == "W" || oldSymbol instanceof MultipleSymbolsThing) ||
                                     ((oldSymbolName == "TA" || oldSymbolName == "MA") && posIndex > 4));

                // collector symbol blinking
                const collectorSymbol = <CollectorSymbol>BrickSnake2000.freespinsReelView.getAllSymbols()
                                                                                         .find((sym) => sym.symbolName == "TA")
                if (BrickSnake2000.model.data.mode == GameMode.FREESPIN_SUPER &&
                    !alreadyEatenSymbols.has(collectorSymbol)) {
                    if (posIndex < 5) {
                        stepTl.add(() => {
                            collectorSymbol.setBrokenLevel(posIndex);
                            BrickSnake2000.soundPlayer.playSound("walletGoBad");
                        })
                    } else if (posIndex == 5) {
                        const collectorPos = BrickSnake2000.model.getCollectorSymbolPosition();
                        const collectorReelView = BrickSnake2000.freespinsReelView.getReelModel(collectorPos.reelID, collectorPos.reelPosition);
                        alreadyEatenSymbols.add(collectorSymbol);
                        stepTl.add(() => {
                            const newSymbol = BrickSnake2000.symbolFactory.createSymbol({
                                symbolName: "X",
                                isSpinning: false,
                                isStopping: false,
                                reelsView: collectorReelView,
                                reelPosition: collectorPos.reelPosition,
                                reelID: collectorPos.reelID,
                                symbolSize: 1
                            })
                            BrickSnake2000.freespinsReelView.replaceSymbolAt(collectorPos.reelID, collectorPos.reelPosition, newSymbol);
                            BrickSnake2000.soundPlayer.playSound("walletGone");
                        })
                        // extra padding to make sure you can see the collector symbol disappear before the snakes
                        // starts moving
                        stepTl.add(() => {}, '>0.6');
                    }
                }

                // remove max coin
                if (posIndex == 5) {
                    const maxWinSymbol = <MaxWinSymbol>BrickSnake2000.freespinsReelView.getAllSymbols()
                                                                                       .find((sym) => sym.symbolName == "MA")
                    if (maxWinSymbol != undefined && !alreadyEatenSymbols.has(maxWinSymbol)) {
                        const coinPos = BrickSnake2000.model.getFreespinsMaxWinPosition();
                        const coinReelView = BrickSnake2000.freespinsReelView.getReelModel(coinPos.reelID, coinPos.reelPosition);
                        alreadyEatenSymbols.add(maxWinSymbol);
                        stepTl.add(() => {
                            const newSymbol = BrickSnake2000.symbolFactory.createSymbol({
                                symbolName: "X",
                                isSpinning: false,
                                isStopping: false,
                                reelsView: coinReelView,
                                reelPosition: coinPos.reelPosition,
                                reelID: coinPos.reelID,
                                symbolSize: 1
                            })
                            BrickSnake2000.freespinsReelView.replaceSymbolAt(coinPos.reelID, coinPos.reelPosition, newSymbol);
                            BrickSnake2000.soundPlayer.playSound("walletGone");
                        })
                        // extra padding to make sure you can see the collector symbol disappear before the snakes
                        // starts moving
                        stepTl.add(() => {}, '>0.6');
                    }
                }

                // move snake
                // spawn snake that came from egg
                if (cameFromEgg) {
                    stepTl.add(() => {
                        const factory = BrickSnake2000.symbolFactory;
                        const newSymbolSuffix = `_Head${snakeSkin}`;
                        const newSymbol = factory.createSymbol(Object.assign({suffix: newSymbolSuffix}, symbolArgs));
                        (<BrickSnake2000WildSymbol>newSymbol).setSnakeSkin(snakeSkin);
                        (<BrickSnake2000WildSymbol>newSymbol).setBodyPart("_Head");
                        (<BrickSnake2000WildSymbol>newSymbol).setRotation(0);
                        BrickSnake2000.freespinsReelView.addSnakeAt(prevPos.reelID, prevPos.reelPosition, newSymbol);
                        BrickSnake2000.soundPlayer.playSound("snekBornAgain");
                    })
                    stepTl.add(() => {}, '>0.6');
                }

                // needed to set state on the same symbol in 2 different lambdas, shouldnt really be a problem
                // just create the symbol and show it later but due to how the entire reelView interface
                // for the reels that cant be done in a nice way, in fact it would be just as messy
                // as the code requiring this hackjob
                const newSymbolHackjob:{sym?:BrickSnake2000Symbol} = {};
                stepTl.add(() => {
                    const factory = BrickSnake2000.symbolFactory;

                    if (oldSymbolName != "K") {
                        const newSymbolSuffix = `_Head${snakeSkin}`;
                        const newSymbol = factory.createSymbol(Object.assign({suffix: newSymbolSuffix}, symbolArgs));
                        (<BrickSnake2000WildSymbol>newSymbol).setSnakeSkin(snakeSkin);
                        (<BrickSnake2000WildSymbol>newSymbol).setBodyPart("_Head");
                        (<BrickSnake2000WildSymbol>newSymbol).setRotation(rotation, cameFromDirection);
                        if (snakeHasNotBeenHere)
                            (<BrickSnake2000WildSymbol>newSymbol).setEating();
                        BrickSnake2000.freespinsReelView.addSnakeAt(pos.reelID, pos.reelPosition, newSymbol);

                        newSymbolHackjob.sym = newSymbol;
                    }

                    const newPrevSymbolSuffix = `${prevSymbolNewPart}${snakeSkin}`;
                    const newPrevSymbol = factory.createSymbol(Object.assign({suffix: newPrevSymbolSuffix},
                                                                             symbolArgs,
                                                                             {reelsView: reelViewPrev}));
                    (<BrickSnake2000WildSymbol>newPrevSymbol).setRotation(prevRotation);
                    (<BrickSnake2000WildSymbol>newPrevSymbol).setSnakeSkin(snakeSkin);
                    (<BrickSnake2000WildSymbol>newPrevSymbol).setBodyPart(prevSymbolNewPart);
                    BrickSnake2000.freespinsReelView.addSnakeAt(prevPos.reelID, prevPos.reelPosition, newPrevSymbol);
                    BrickSnake2000.freespinsReelView.removeSneakHeadAt(prevPos.reelID, prevPos.reelPosition, snakeSkin);

                    if (oldSymbolName == "K") {
                        BrickSnake2000.freespinsReelView.addDeadSnek(pos);
                        BrickSnake2000.freespinsReelView.getAllSymbols()
                            .filter((sym) => sym.symbolName == "W")
                            .filter((sym) => (<BrickSnake2000WildSymbol>sym).getSnakeSkin() == snakeSkin)
                            .forEach((sym) => {
                                (<BrickSnake2000WildSymbol>sym).dieForReal();
                            })
                    }

                    BrickSnake2000.soundPlayer.playSound((this.snakeSoundIndex++ % 2 == 0) ? "snekMove1" : "snekMove2");
                    if (oldSymbolName != "X" && canBeEaten) {
                        const eatSound = oldSymbolName == "SE"
                                         ? "snekEgg"
                                         : oldSymbolName == "K"
                                         ? "slayerKillsSnek"
                                         : oldSymbolName == "MA"
                                         ? "snekEatsMaxWinSym"
                                         : "snekEat";
                        BrickSnake2000.soundPlayer.playSound(eatSound);
                    }
                });

                // eat animation
                if (snakeHasNotBeenHere) {
                    stepTl.add(BrickSnake2000.freespinsReelView.doEatAnimation(pos), '<');
                    if (oldSymbolName != "X" && canBeEaten) {
                        stepTl.add(BrickSnake2000.freespinsReelView.doEatThingAnimation(pos), '<');
                    }
                }

                if (oldSymbolName != "K" && wasFinalStep && (oldSymbolName == "X" || !snakeHasNotBeenHere)){
                    stepTl.add(() => {
                        const newSymbol = newSymbolHackjob.sym;
                        (<BrickSnake2000WildSymbol>newSymbol).breakNeckStep1();
                        (<BrickSnake2000WildSymbol>newSymbol).moveToWinLayer();
                        BrickSnake2000.soundPlayer.playSound("snekNeckSnap");
                    }, '<0.25');
                    stepTl.add(() => {
                        const newSymbol = newSymbolHackjob.sym;
                        (<BrickSnake2000WildSymbol>newSymbol).breakNeckStep2();
                    }, '<0.15');
                }

                stepTl.add(() => {
                    const newSymbol = newSymbolHackjob.sym;
                    if (oldSymbolName != "K") {
                        if (snakeHasNotBeenHere && !(wasFinalStep && oldSymbolName == "X"))
                            (<BrickSnake2000WildSymbol>newSymbol).setNotEating();

                        if (wasFinalStep && (oldSymbolName == "X" || !snakeHasNotBeenHere)) {
                            BrickSnake2000.freespinsReelView.getAllSymbols()
                                .filter((sym) => sym.symbolName == "W")
                                .filter((sym) => (<BrickSnake2000WildSymbol>sym).getSnakeSkin() == snakeSkin)
                                .forEach((sym) => {
                                    (<BrickSnake2000WildSymbol>sym).die();
                                })
                            BrickSnake2000.soundPlayer.playSound("snekEatsSelf");
                            BrickSnake2000.freespinsReelView.correctChildrenAt(pos.reelID, pos.reelPosition);
                        }
                    }
                }, '<0.25')

                // play animation on slayer when snake dies
                if (oldSymbolName == "K") {
                    stepTl.add(oldSymbol.getLandAnimation(), '<-0.25');
                }

                // explosion
                if (brokeWall) {
                    stepTl.add(BrickSnake2000.freespinsReelView.doWallExplosion(prevPos, pos), '<-0.25');
                    stepTl.add(() => {BrickSnake2000.soundPlayer.playSound("snekThroughWall")}, '<');
                }

                // eat stuff
                if (!alreadyEatenSymbols.has(oldSymbol)) {
                    alreadyEatenSymbols.add(oldSymbol);

                    if (oldSymbolName == "MA") {
                        stepTl.add(BrickSnake2000.freespinsReelView.blinkCoinCounter(), '>0.4');
                        stepTl.add(() => {
                            BrickSnake2000.freespinsReelView.addToWallet(8110);
                            BrickSnake2000.soundPlayer.playSound("coinDisplayUpdate");
                        }, '<')
                        stepTl.add(() => {
                            BrickSnake2000.soundPlayer.fadeOutMusic();
                        })
                        stepTl.add(() => {}, '>0.8');
                    }

                    if (oldSymbolName == "MU") {
                        stepTl.add(BrickSnake2000.freespinsReelView.blinkCoinCounter(), '>0.4');
                        stepTl.add(() => {
                            BrickSnake2000.freespinsReelView.addToWallet((<FreespinsMultiplierSymbol>oldSymbol).multiplierValue);
                            BrickSnake2000.soundPlayer.playSound("coinDisplayUpdate");
                        }, '<')
                        stepTl.add(() => {}, '>0.2');
                    }

                    if (oldSymbolName == "TA") {
                        stepTl.add(BrickSnake2000.freespinsReelView.blinkCoinCounter(), '>0.4');
                        stepTl.add(() => {
                            BrickSnake2000.freespinsReelView.addToWallet((<CollectorSymbol>oldSymbol).multiplierValue);
                            BrickSnake2000.soundPlayer.playSound("coinDisplayUpdateRassle");
                        }, '<')

                        stepTl.add(BrickSnake2000.freespinsReelView.blinkFsMultiplier(), '>1');
                        stepTl.add(() => {
                            BrickSnake2000.soundPlayer.playSound("multiplierDisplayRassle");
                        }, '<');
                        stepTl.add(BrickSnake2000.freespinsReelView.blinkCollectorWallet(), '>0.4')
                        stepTl.add(() => {
                            BrickSnake2000.soundPlayer.playSound("walletUpdateRassle");
                            BrickSnake2000.freespinsReelView.setCollectCounter(BrickSnake2000.model.currentCollectorValue);
                        }, '<');
                        stepTl.add(() => {}, ">0.2");
                    }

                    if (oldSymbolName == "WM") {
                        const multiplierHackjob = {oldMultiplier: 0};
                        const additionalMultiplier = (<FreespinsMultiplierSymbol>oldSymbol).multiplierValue;

                        stepTl.add(BrickSnake2000.freespinsReelView.blinkFsMultiplier(), '>0.4');
                        stepTl.add(() => {
                            multiplierHackjob.oldMultiplier = BrickSnake2000.freespinsReelView.getFsMultiplier();
                            BrickSnake2000.freespinsReelView.addtoFsMultiplier(additionalMultiplier);
                            BrickSnake2000.soundPlayer.playSound("multiplierDisplayUpdate");
                        }, '<')
                        stepTl.add(() => {
                            const oldMultiplier = multiplierHackjob.oldMultiplier;
                            const newMultiplier = oldMultiplier + additionalMultiplier;
                            BrickSnake2000.freespinsReelView.getAllSymbols()
                                                            .filter((sym) => sym.symbolName == "MU")
                                                            .forEach((sym) => {
                                                                (<FreespinsMultiplierSymbol>sym).updateValue(oldMultiplier, newMultiplier)
                                                            })
                            BrickSnake2000.soundPlayer.playSound("coinMultiUpdate");
                        }, '>0.4');
                        stepTl.add(() => {}, '>0.4');
                    }

                }

                // extra neck break opportunity in case snek actually ate something on its final spin
                if (oldSymbolName != "K" && wasFinalStep && oldSymbolName != "X" && snakeHasNotBeenHere) {
                    stepTl.add(() => {
                       const newSym = <BrickSnake2000WildSymbol>(newSymbolHackjob.sym);
                       newSym.setEating();
                    })
                    stepTl.add(() => {
                        const newSymbol = newSymbolHackjob.sym;
                        (<BrickSnake2000WildSymbol>newSymbol).breakNeckStep1();
                        (<BrickSnake2000WildSymbol>newSymbol).moveToWinLayer();
                        BrickSnake2000.soundPlayer.playSound("snekNeckSnap");
                    }, '<0.25');
                    stepTl.add(() => {
                        const newSymbol = newSymbolHackjob.sym;
                        (<BrickSnake2000WildSymbol>newSymbol).breakNeckStep2();
                    }, '<0.15');

                    stepTl.add(() => {
                        BrickSnake2000.freespinsReelView.getAllSymbols()
                            .filter((sym) => sym.symbolName == "W")
                            .filter((sym) => (<BrickSnake2000WildSymbol>sym).getSnakeSkin() == snakeSkin)
                            .forEach((sym) => {
                                (<BrickSnake2000WildSymbol>sym).die();
                            })
                        BrickSnake2000.soundPlayer.playSound("snekEatsSelf");
                        BrickSnake2000.freespinsReelView.correctChildrenAt(pos.reelID, pos.reelPosition);
                    }, '<0.25')
                }

                stepTl.add(() => {}, '>0.2');

                snakeVisitedPositions[pos.reelID][pos.reelPosition] = true;
            }
            tl.add(stepTl);
        }
        //})
        tl.addLabel("End")
        tl.add(() => {}, '>0.3');

        tl.add(() => {
            controller.setSkipCallback(() => {
                tl.seek("End", false)
            });
        }, 0.25);
    }
}
